import { AppError, createSystemError, isError, SystemError } from "../../core/error"
import { ApiErrorResponse, FetchErrorResponse, isErrorResponse } from "../../models/errorResponse"
import { AxiosError } from "axios"
import { tNamespace } from "../../locales/error"
import { safeJsonParse } from "./safeJsonParse"
import i18n from "i18next"

export const isAxiosError = (error: unknown): error is AxiosError => {
    if (typeof error !== "object" || error === null) {
        return false
    }

    return (
        "config" in error &&
        typeof (error as AxiosError).config === "object" &&
        "isAxiosError" in error &&
        "toJSON" in error &&
        typeof (error as AxiosError).toJSON === "function"
    )
}

export const isFetchError = (error: unknown): error is FetchErrorResponse => {
    if (typeof error !== "object" || error === null) {
        return false
    }

    return (
        "error" in error &&
        typeof (error as FetchErrorResponse).error === "object" &&
        "data" in (error as FetchErrorResponse).error &&
        (error as FetchErrorResponse).error.data !== null &&
        "status" in (error as FetchErrorResponse).error
    )
}

const KEY_TO_LOCALIZE_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/
const getReason = (defaultReason = "", path: string): string => {
    const reasons = defaultReason.split("\n")

    const localizedReasons = reasons.map(reason => {
        const isNeedLocalization = KEY_TO_LOCALIZE_REGEX.test(reason)

        if (isNeedLocalization) {
            return `${tNamespace}${path}${reason}`
        }

        return reason
    })

    return localizedReasons.join("\n")
}

const extractLocalizePath = (path: string): string =>
    path
        .split(".")
        .slice(1)
        .map(segment => segment.replace(/(?!^)([A-Z])/g, "-$1").toLowerCase())
        .join(".")

const localizeError = (error: unknown, localizeReasonPath: string): string => {
    if (!isFetchError(error)) {
        return JSON.stringify(error)
    }

    const { Errors: errors } = safeJsonParse<ApiErrorResponse>(error.error.data as string, { Errors: [] })

    if (errors.length === 0) {
        return JSON.stringify(error)
    }

    return errors
        .map(err => i18n.t(`${tNamespace}${localizeReasonPath}${extractLocalizePath(err.Key)}`, err.Params))
        .join("\n")
}

const apiCallExceptionToAppError = (error: unknown, localizeReasonPath: string): AppError => {
    if (isAxiosError(error)) {
        const errorResponse = error.response

        if (!errorResponse || !errorResponse.data) {
            return error
        }

        if (isErrorResponse(errorResponse.data)) {
            const error = errorResponse.data.error
            return {
                reason: getReason(error.message, localizeReasonPath),
                reasonKey: `${tNamespace}${error.key}`
            }
        }

        if (error.message) {
            return error.message
        }

        return JSON.stringify(errorResponse.data)
    }

    if (isError(error)) {
        return error
    }

    return localizeError(error, localizeReasonPath)
}

export const apiCallExceptionToSystemError = (
    messageKey: string,
    error: unknown,
    localizeReasonPath = ""
): SystemError => {
    return createSystemError(messageKey, apiCallExceptionToAppError(error, localizeReasonPath))
}
